import { useEffect, useRef } from "react";

// Starts the next batch a screen ahead, so the user rarely sees the bottom of the grid.
const SENTINEL_ROOT_MARGIN = "400px";

type UseEndOfListSentinelParams = {
  enabled: boolean;
  onEndReached: () => void;
};

// Only reports that the bottom is visible: useMediaFiles owns the guards against duplicate requests.
// Reconnecting whenever `enabled` flips back on repeats the initial intersection check, so a sentinel that
// stays in view after a batch lands asks for the next one.
export default function useEndOfListSentinel({ enabled, onEndReached }: UseEndOfListSentinelParams) {
  const refSentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinelElement = refSentinel.current;
    if (!enabled || !sentinelElement) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries.some((entry) => entry.isIntersecting)) {
          onEndReached();
        }
      },
      { rootMargin: SENTINEL_ROOT_MARGIN },
    );
    observer.observe(sentinelElement);
    return () => observer.disconnect();
  }, [enabled, onEndReached]);

  return refSentinel;
}
